/**
 * 인쇄 — 자가측정 성적서 고르기
 * ---------------------------------------------------------------------------
 * 별지 제7호서식의 "3. 자가측정사항" 은 측정을 대행업자에게 전부 위탁하면
 * 작성하지 않고, 보관 중인 성적서로 갈음한다. 인쇄할 때 그 한 건을 고른다.
 *
 * 기본값은 가장 최근 것
 *   거의 항상 최신 성적서를 붙인다. 매번 고르게 하면 같은 선택을 반복하다가
 *   결국 아무거나 누르게 된다. 골라 둔 채로 열고, 바꿀 수 있게 한다.
 *
 * 여러 사업장을 한 번에 인쇄할 때
 *   고를 수 없게 둔다. 한 건을 골라 전부에 붙이면 A 사업장 서류가 B 사업장
 *   기록부에 실린다 — 제출 서류에서 그건 사고다. 서버가 사업장마다 그
 *   사업장의 최신 성적서를 붙인다.
 */

import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Alert, Modal, Select, Space, Typography } from 'antd';
import type { SelfMeasurement } from '@eco/shared';
import { api } from '../../lib/api';

const { Text } = Typography;

export function PrintDialog({
  open,
  count,
  siteId,
  canChoose,
  loading,
  onCancel,
  onConfirm,
}: {
  open: boolean;
  /** 인쇄할 일지 건수. 확인 버튼에 보여 준다. */
  count: number;
  /** 고른 일지가 모두 한 사업장일 때만 값이 온다. */
  siteId: string | null;
  /** 자가측정 서류 조회 권한이 있는가. */
  canChoose: boolean;
  loading: boolean;
  onCancel: () => void;
  onConfirm: (selfMeasurementId: string | null) => void;
}) {
  const [chosen, setChosen] = useState<string | null>(null);

  const docs = useQuery<SelfMeasurement[]>({
    queryKey: ['self-measurements', siteId],
    queryFn: async () =>
      (await api.get<SelfMeasurement[]>('/self-measurements', { params: { siteId } })).data,
    // 사업장이 하나로 정해졌을 때만 고를 수 있다.
    enabled: open && canChoose && !!siteId,
  });

  // 목록은 측정 시점 내림차순이다. 첫 번째가 가장 최근 것이다.
  useEffect(() => {
    if (docs.data && docs.data.length > 0) setChosen(docs.data[0]!.selfMeasurementId);
    else setChosen(null);
  }, [docs.data]);

  const multiSite = !siteId;

  return (
    <Modal
      open={open}
      title="법정 서식 인쇄"
      okText={`${count}건 인쇄`}
      cancelText="취소"
      okButtonProps={{ loading }}
      onCancel={onCancel}
      onOk={() => onConfirm(multiSite ? null : chosen)}
      width={560}
      destroyOnHidden
    >
      <Space direction="vertical" size={12} style={{ width: '100%' }}>
        <Text type="secondary">
          새 창이 열리고 인쇄 대화상자가 뜹니다. 대상을 &ldquo;PDF로 저장&rdquo; 으로 고르면
          PDF 파일이 됩니다.
        </Text>

        {multiSite ? (
          <Alert
            type="info"
            showIcon
            message="여러 사업장의 일지를 함께 인쇄합니다"
            description="자가측정 성적서는 사업장마다 가장 최근 것이 자동으로 붙습니다."
          />
        ) : !canChoose ? (
          <Alert
            type="info"
            showIcon
            message="자가측정 서류를 조회할 권한이 없어 자동으로 선택됩니다."
          />
        ) : (
          <div>
            <Text strong>3. 자가측정사항에 붙일 성적서</Text>
            {/*
              여기는 "고르는" 자리이지 "올리는" 자리가 아니다. 목록이 비었을
              때만 알리면, 이미 등록해 둔 사람은 어디서 더 올리는지 모른다.
              항상 둔다.
            */}
            <div style={{ margin: '4px 0 6px' }}>
              <Text type="secondary" style={{ fontSize: 12 }}>
                성적서는 <Text code>자가측정 서류관리</Text> 메뉴에서 등록합니다. 여기서는
                등록된 것 중에서 고릅니다.
              </Text>
            </div>
            <Select
              allowClear
              style={{ width: '100%', marginTop: 6 }}
              value={chosen ?? undefined}
              loading={docs.isLoading}
              placeholder={
                (docs.data ?? []).length === 0
                  ? '등록된 성적서가 없습니다'
                  : '성적서를 고르세요'
              }
              onChange={(v) => setChosen(v ?? null)}
              options={(docs.data ?? []).map((d, i) => ({
                value: d.selfMeasurementId,
                label: `${d.measuredYear}년${d.half ? ` ${d.half === 1 ? '상반기' : '하반기'}` : ''} · ${d.title}${i === 0 ? ' (최신)' : ''}`,
              }))}
            />
            {(docs.data ?? []).length === 0 && !docs.isLoading && (
              <Text type="secondary" style={{ fontSize: 12 }}>
                [자가측정 서류관리] 에서 성적서를 등록하면 여기에서 고를 수 있습니다.
              </Text>
            )}
          </div>
        )}
      </Space>
    </Modal>
  );
}
